import { useEffect, useState, type ClipboardEvent } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import ChecklistRow, { AskText } from "./ChecklistRow";
import ConfirmDialog from "../common/ConfirmDialog";
import { useAttachmentUrl } from "../../hooks/useAttachmentUrl";
import { EvidenceAttachments } from "../../hooks/useEvidenceAttachments";
import {
  EvidenceAttachmentRef,
  EvidenceDraftSummary,
  EvidenceGeneratedProduct,
  EvidenceInputOverrides,
  IMAGE_UPLOAD_ACCEPT,
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  imageUploadError,
} from "../../types/evidence";

// hasOwn: a product titled "constructor" must not read Object.prototype's.
export function ownDescription(overrides: EvidenceInputOverrides | null | undefined, title: string): string | null {
  const descriptions = overrides?.productDescriptions ?? {};
  return Object.prototype.hasOwnProperty.call(descriptions, title) && descriptions[title] ? descriptions[title] : null;
}

// What prints now: the agent's text, else Shopify's. The printed copy kept Shopify's beside it,
// '' when Shopify had none, so a cleared override falls back to nothing rather than to the deleted text.
export function currentDescription(overrides: EvidenceInputOverrides | null | undefined, product: EvidenceGeneratedProduct): string | null {
  const own = ownDescription(overrides, product.title);
  if (own) return own;
  return product.shopifyDescription != null ? product.shopifyDescription || null : product.productDescription;
}

export interface ProductRow {
  title: string;
  text: string | null;
  own: boolean;
  /** The store's own image, printed when no photo is attached. */
  imageUrl: string | null;
  attached: EvidenceAttachmentRef | null;
}

export function productRows(draft: EvidenceDraftSummary, attachments: EvidenceAttachmentRef[]): ProductRow[] {
  const seen = new Set<string>();
  return (draft.generated_products ?? []).filter((product) => {
    if (seen.has(product.title)) return false;
    seen.add(product.title);
    return true;
  }).map((product) => ({
    title: product.title,
    text: currentDescription(draft.input_overrides, product),
    own: ownDescription(draft.input_overrides, product.title) !== null,
    imageUrl: product.imageUrl ?? null,
    attached: attachments.find((a) => a.slot === "product_photo" && a.productTitle === product.title) ?? null,
  }));
}

function PhotoFrame({ src, size, label }: { src: string | null; size: number; label: string }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 1,
        border: "1px",
        borderStyle: src ? "solid" : "dashed",
        borderColor: "divider",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {src ? (
        <Box component="img" src={src} alt={label} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", px: 0.5 }}>No photo</Typography>
      )}
    </Box>
  );
}

/** The photo the row prints now: the attached one, else the store's. */
function RowPhoto({ draftId, row }: { draftId: number; row: ProductRow }) {
  const attachedUrl = useAttachmentUrl(draftId, row.attached?.storageKey ?? null);
  return <PhotoFrame src={row.attached ? attachedUrl : row.imageUrl} size={44} label={`Photo of ${row.title}`} />;
}

type PhotoChange = { kind: "keep" } | { kind: "file"; file: File; previewUrl: string } | { kind: "remove" };

// One dialog for both: the text and the photo save together, so the operator reviews one product
// at a time and the row reads what the document prints.
function ProductDialog({ draftId, row, attachments, open, onClose, onSave }: {
  draftId: number;
  row: ProductRow;
  attachments: EvidenceAttachments;
  open: boolean;
  onClose: () => void;
  onSave: (text: string) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState(row.text ?? "");
  const [change, setChange] = useState<PhotoChange>({ kind: "keep" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const attachedUrl = useAttachmentUrl(draftId, row.attached?.storageKey ?? null);

  useEffect(() => {
    if (open) {
      setDraft(row.text ?? "");
      setChange({ kind: "keep" });
      setError(null);
      setConfirmDiscard(false);
    }
  }, [open, row.text]);

  // Up to 2000 characters of written text, and a picked photo, with no recovery once they are gone.
  const edited = draft.trim() !== (row.text ?? "") || change.kind !== "keep";
  const requestClose = () => { if (edited) setConfirmDiscard(true); else onClose(); };

  // A picked file's preview is an object URL: released when another replaces it or the dialog goes.
  useEffect(() => () => { if (change.kind === "file") URL.revokeObjectURL(change.previewUrl); }, [change]);

  const pick = (file: File) => {
    const failure = imageUploadError(file, "The photo");
    if (failure) {
      setError(failure);
      return;
    }
    setError(null);
    setChange({ kind: "file", file, previewUrl: URL.createObjectURL(file) });
  };

  const { getRootProps, getInputProps, isDragActive, open: browse } = useDropzone({
    accept: Object.fromEntries(IMAGE_UPLOAD_ACCEPT.split(",").map((type) => [type, []])),
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: saving,
    // A file the accept filter refused still needs its reason shown.
    onDrop: (accepted, rejections) => {
      if (rejections[0]) setError(imageUploadError(rejections[0].file, "The photo") ?? rejections[0].errors[0].message);
      else if (accepted[0]) pick(accepted[0]);
    },
  });

  // Paste anywhere in the dialog: an image on the clipboard is the photo, text still goes to the field.
  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const file = Array.from(event.clipboardData.files).find((candidate) => candidate.type.startsWith("image/"));
    if (!file) return;
    event.preventDefault();
    pick(file);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const text = draft.trim();
    if (text !== (row.text ?? "") && !(await onSave(text))) {
      setSaving(false);
      return;
    }
    const failure = change.kind === "file"
      ? await attachments.upload("product_photo", `product_photo-${row.title}`, change.file, row.title)
      : change.kind === "remove" && row.attached
        ? await attachments.remove(row.attached.storageKey)
        : null;
    setSaving(false);
    if (failure) {
      setError(failure);
      return;
    }
    onClose();
  };

  // What the frame shows: the picked file, nothing when removing, else what prints now.
  const preview = change.kind === "file"
    ? change.previewUrl
    : change.kind === "remove"
      ? null
      : row.attached ? attachedUrl : row.imageUrl;
  const status = change.kind === "file"
    ? `New: ${change.file.name}`
    : change.kind === "remove"
      ? "Removed on save"
      : row.attached
        ? `Attached: ${row.attached.fileName}`
        : row.imageUrl ? "From the store" : "Printed on the order receipt";
  const removable = change.kind === "file" || (change.kind === "keep" && row.attached !== null);

  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== "backdropClick" && !saving) requestClose(); }} fullWidth maxWidth="sm">
      <DialogTitle>{row.title}</DialogTitle>
      <DialogContent onPaste={onPaste}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Printed in the Product Description box. Leave the text empty to print the order&apos;s own description again.
        </Typography>
        <TextField
          id={`product-description-${row.title}`}
          label="Product description"
          multiline
          minRows={5}
          fullWidth
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          slotProps={{ htmlInput: { maxLength: PRODUCT_DESCRIPTION_MAX_LENGTH } }}
          helperText={
            <Box component="span" sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <span>Saved for every dispute of this company with this product.</span>
              <span>{`${draft.length} / ${PRODUCT_DESCRIPTION_MAX_LENGTH}`}</span>
            </Box>
          }
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mt: 2 }}>
          <Box
            {...getRootProps()}
            sx={{
              borderRadius: 1,
              outline: isDragActive ? "2px solid" : "none",
              outlineColor: "primary.main",
              outlineOffset: 2,
            }}
          >
            <input {...getInputProps({ "aria-label": "Choose a photo" })} />
            <PhotoFrame src={preview} size={120} label={`Photo of ${row.title}`} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Product photo</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{status}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Replaces the store image on the receipt. Drop or paste a PNG or JPEG up to 5 MB.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={browse} disabled={saving}>
                {preview ? "Replace" : "Choose file"}
              </Button>
              {removable && (
                <Button size="small" color="error" onClick={() => setChange({ kind: "remove" })} disabled={saving}>Remove</Button>
              )}
              {change.kind !== "keep" && (
                <Button size="small" onClick={() => setChange({ kind: "keep" })} disabled={saving}>Undo</Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard your changes?"
        body="The changes to this product have not been saved."
        confirmLabel="Discard"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={onClose}
      />
    </Dialog>
  );
}

export default function ProductDescriptionCard({ draft, attachments, disabled, onSave, embedded }: {
  draft: EvidenceDraftSummary;
  attachments: EvidenceAttachments;
  disabled: boolean;
  onSave: (product: string, text: string) => Promise<boolean>;
  /** Inside a section row that already names it: no heading and no spacing of its own. */
  embedded?: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => { setEditing(null); }, [draft.draft_id]);

  const rows = productRows(draft, attachments.attachments);
  const generated = draft.generated_at !== null;
  const open = rows.find((row) => row.title === editing) ?? null;

  const photoStatus = (row: ProductRow) => (row.attached ? "photo attached" : row.imageUrl ? "photo from the store" : "no photo");

  return (
    <>
      {!embedded && <Typography variant="subtitle2" sx={{ mt: 2 }}>Product description</Typography>}
      {!generated ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: embedded ? 0 : 1 }}>
          Generate the evidence first — the products are read from the order.
        </Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {rows.map((row) => {
            const status = `${row.text ? (row.own ? "Saved for the company" : "From the order") : "No description on the order"} · ${photoStatus(row)}`;
            // The photo sits flush with the row body; a missing description colours its own line.
            return (
              <ChecklistRow
                key={row.title}
                leading={<RowPhoto draftId={draft.draft_id} row={row} />}
                primary={row.title}
                secondary={<AskText severity={row.text ? null : "recommended"}>{status}</AskText>}
                action={(
                  <Button size="small" disabled={disabled || attachments.disabled} onClick={() => setEditing(row.title)}>
                    Edit
                  </Button>
                )}
              />
            );
          })}
        </Box>
      )}
      {open && (
        <ProductDialog
          draftId={draft.draft_id}
          row={open}
          attachments={attachments}
          open
          onClose={() => setEditing(null)}
          onSave={(text) => onSave(open.title, text)}
        />
      )}
    </>
  );
}
